/**
 * Maestro Animal — Regalo de primer pedido
 *
 * Añade y quita automáticamente la lata de regalo del carrito según el
 * contenido del mismo, apoyándose en las promociones automáticas
 * "Compra X, llévate Y" configuradas en Shopify (que hacen la lata gratis).
 *
 * Este script NO decide el precio ni el descuento: solo se encarga de que
 * la lata correcta esté físicamente en el carrito para que el descuento
 * automático de Shopify pueda aplicarse.
 *
 * Si el cliente tiene un código de descuento de pedido aplicado (p. ej. el
 * código de amigos), Shopify no combina ese código con la promoción
 * automática del regalo, así que la lata dejaría de ser gratis. En ese caso
 * no se añade la lata, y si ya estaba en el carrito se retira.
 */
(function () {
  const GIFT_VARIANTS = {
    dog: 58391628808579, // Lenda Paté Dog Conejo con Zanahorias (400g)
    cat: 58391630774659, // Lenda Paté Cat Pollo con Buey y Arándanos (400g)
  };

  function isGiftVariant(variantId) {
    const id = Number(variantId);
    return id === GIFT_VARIANTS.dog || id === GIFT_VARIANTS.cat;
  }

  function speciesFromProductType(productType) {
    if (!productType) return null;
    const t = productType.toLowerCase();
    if (t.includes('perro')) return 'dog';
    if (t.includes('gato')) return 'cat';
    return null;
  }

  function getCartElement() {
    return document.querySelector('cart-drawer') || document.querySelector('cart-notification');
  }

  function buildSectionsPayload() {
    const cartEl = getCartElement();
    const sections = new Set(['main-cart-items', 'main-cart-footer']);
    if (cartEl && typeof cartEl.getSectionsToRender === 'function') {
      cartEl.getSectionsToRender().forEach((section) => sections.add(section.id));
    }
    return {
      sections: Array.from(sections),
      sections_url: window.location.pathname,
    };
  }

  function swapSection(sectionHtml, selector) {
    const parser = new DOMParser();
    const newDoc = parser.parseFromString(sectionHtml, 'text/html');
    const newContents = newDoc.querySelector(selector);
    const oldContents = document.querySelector(selector);
    if (newContents && oldContents) {
      oldContents.replaceWith(newContents);
    }
  }

  function renderCartSections(data) {
    if (!data || !data.sections) return;

    const cartEl = getCartElement();
    if (cartEl && typeof cartEl.renderContents === 'function') {
      cartEl.renderContents(data);
    }

    // Si el cliente está en la página /cart, refrescamos también la tabla de
    // productos y el total directamente, sin recargar la página.
    if (data.sections['main-cart-items']) {
      swapSection(data.sections['main-cart-items'], '#main-cart-items .js-contents');
    }
    if (data.sections['main-cart-footer']) {
      swapSection(data.sections['main-cart-footer'], '#main-cart-footer .js-contents');
    }
  }

  async function addGift(species) {
    const variantId = GIFT_VARIANTS[species];
    if (!variantId) return;

    const body = Object.assign(
      {
        items: [
          {
            id: variantId,
            quantity: 1,
            properties: { _regalo_primer_pedido: 'true' },
          },
        ],
      },
      buildSectionsPayload()
    );

    try {
      const response = await fetch((window.routes && window.routes.cart_add_url) || '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await response.json();
      renderCartSections(data);
    } catch (error) {
      console.error('[Regalo primer pedido] No se pudo añadir la lata de regalo:', error);
    }
  }

  async function removeGift(species) {
    const variantId = GIFT_VARIANTS[species];
    if (!variantId) return;

    const body = Object.assign({ id: variantId, quantity: 0 }, buildSectionsPayload());

    try {
      const response = await fetch((window.routes && window.routes.cart_change_url) || '/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await response.json();
      renderCartSections(data);
    } catch (error) {
      console.error('[Regalo primer pedido] No se pudo quitar la lata de regalo:', error);
    }
  }

  let syncing = false;

  async function syncGifts() {
    if (syncing) return;
    syncing = true;

    try {
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();
      const items = cart.items || [];

      const hasDiscountCode = (cart.cart_level_discount_applications || []).length > 0;

      const hasDog = items.some(
        (item) => !isGiftVariant(item.variant_id) && speciesFromProductType(item.product_type) === 'dog'
      );
      const hasCat = items.some(
        (item) => !isGiftVariant(item.variant_id) && speciesFromProductType(item.product_type) === 'cat'
      );

      const dogGiftInCart = items.some((item) => Number(item.variant_id) === GIFT_VARIANTS.dog);
      const catGiftInCart = items.some((item) => Number(item.variant_id) === GIFT_VARIANTS.cat);

      // Con un código de descuento de pedido activo, la lata no puede ser
      // gratis (Shopify no combina descuentos de pedido con la promoción
      // automática del regalo), así que se trata como si no hubiera que
      // regalarla.
      const shouldHaveDog = hasDog && !hasDiscountCode;
      const shouldHaveCat = hasCat && !hasDiscountCode;

      if (shouldHaveDog && !dogGiftInCart) {
        await addGift('dog');
      } else if (!shouldHaveDog && dogGiftInCart) {
        await removeGift('dog');
      }

      if (shouldHaveCat && !catGiftInCart) {
        await addGift('cat');
      } else if (!shouldHaveCat && catGiftInCart) {
        await removeGift('cat');
      }
    } catch (error) {
      console.error('[Regalo primer pedido] No se pudo comprobar el carrito:', error);
    } finally {
      syncing = false;
    }
  }

  function init() {
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
        syncGifts();
      });
    }
    // También comprobamos al cargar la página (ej. si el cliente recarga
    // con productos ya en el carrito pero sin haber pasado por este script).
    syncGifts();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
